import React, { useRef, useState, useEffect } from 'react';
import Slider from 'react-slick';
import styled from '@emotion/styled';
import { css } from '@emotion/core';
import fonts from 'style/fonts';
import { mq, baseTransitionSecond, easeIn } from 'style/variables';
import { text, contentWrapper } from 'style/mixin';
import sizes from 'style/sizes';
import colors from 'style/colors';
import Img from 'components/atoms/img';
import { Video } from 'components/atoms/video';
import arrowIcon from 'assets/images/icons/icon-arrow-long.svg';
import { TweenMax } from 'gsap';

type GallerySlideProps = {
  type?: 'full' | 'normal';
  contents: Array<ContentsObject>;
  loop?: boolean;
  rtl?: boolean;
};

export type ContentsObject = {
  media: 'image' | 'video';
  path: string;
  title?: string;
  alt?: string;
};

type ArrowObject = {
  slideType?: 'full' | 'normal';
  dir?: 'prev' | 'next';
  onClick?: (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => any;
};
type ArrowInnerObject = {
  slideType?: 'full' | 'normal';
  dir?: 'prev' | 'next';
};

const AUTOPLAYSPEED = 5000;

const Container = styled.div`
  margin-bottom: ${sizes.margin[209]};
  overflow: hidden;
  ${mq.onlysp} {
    margin-bottom: ${sizes.margin[100]};
  }
`;

const SliderWrapper = styled.div<GallerySlideProps>`
  position: relative;
  margin-bottom: ${sizes.margin[32]};
  ${props =>
    props.type !== 'full' &&
    css`
      ${mq.onlypc} {
        ${contentWrapper}
        margin-bottom: ${sizes.margin[32]};
      }
    `};
  ${props =>
    props.loop &&
    css`
      margin-bottom: 0;
    `};
  .slick-track {
    will-change: transform;
  }
  .slick-arrow {
    &:hover {
      &::after {
        opacity: 0.3;
      }
    }
    &::before,
    &::after {
      content: '';
      display: block;
      position: absolute;
    }
    &::before {
      right: -38px;
      top: auto;
      bottom: ${({ type }) => (type === 'full' ? (261 / 655) * 100 : (180 / 655) * 100)}%;
      width: 66px;
      height: 7px;
      background-image: url(${arrowIcon});
      background-repeat: no-repeat;
      background-position: center;
      opacity: 1;
      filter: drop-shadow(1px 1px 0 #fff);
      transition: transform ${baseTransitionSecond}s ${easeIn};
      left: 0;
      
    }
    &::after {
      top: 0;
      bottom: 0;
      right: 0;
      left: 0;
      opacity: 0;
      mix-blend-mode: multiply;
      background-color: #3b4959;
      transition: opacity ${baseTransitionSecond}s ${easeIn};
      
    }
    &.slick-prev {
      z-index: 1;
      opacity: 1;
      &::before {
        right: auto;
        left: ${({ type }) => (type === 'full' ? 89 : -38)}px;
        transform: rotate(180deg);
      }
      &:hover {
        &::before {
          transform: rotate(180deg) translate3d(20%, 0, 0);
        }
      }
    }
    &.slick-next {
      right: 0;
      opacity: 1;
      &:before {
        left: auto;
        right: ${({ type }) => (type === 'full' ? 89 : -38)}px;
      }
      &:hover {
        &::before {
          transform: translate3d(20%, 0, 0);
        }
      }
    }
    &.slick-disabled {
      opacity: 0;
    }
  }
`;

const Controler = styled.div`
  display: flex;
  justify-content: space-between;
  ${contentWrapper}
`;

const Title = styled.h4`
  font-family: ${fonts.jaGothic};
  ${text(15, 2)}
`;

const ControlerInner = styled.div``;

const Index = styled.div`
  margin-bottom: ${sizes.margin[32]};
  font-family: ${fonts.enGothic};

  font-size: 0;
  text-align: right;
  ${mq.onlysp} {
    margin-bottom: ${sizes.margin[17]};
  }
  span {
    font-size: 15px;
    line-height: 1;
    &:last-of-type {
      color: #bababa;
      &::before {
        content: '';
        display: inline-block;
        width: 1px;
        height: 10px;
        margin: 0 0.4em 0 0.8em;
        transform: rotate(20deg);
        background-color: ${colors.gray02};
      }
    }
  }
`;

const Progressbars = styled.div`
  position: relative;
  width: 150px;
  height: 1px;
  margin-left: auto;
  background-color: ${colors.gray02};
  ${mq.onlysp} {
    width: 60px;
  }
`;

const Progressbar = styled.div`
  position: absolute;
  top: 0;
  bottom: 0;
  left: 0;
  right: 0;
  transform: scaleX(0);
  background-color: ${colors.black01};
  animation: progressbarAnime02 ${baseTransitionSecond}s linear both;
  &.is-active {
    animation: progressbarAnime01 ${AUTOPLAYSPEED / 1000}s linear both;
  }

  @keyframes progressbarAnime01 {
    0% {
      transform: scaleX(0);
      transform-origin: left;
    }
    100% {
      transform: scaleX(1);
      transform-origin: left;
    }
  }
  @keyframes progressbarAnime02 {
    0% {
      transform: scaleX(1);
      transform-origin: right;
    }
    100% {
      transform: scaleX(0);
      transform-origin: right;
    }
  }
`;

const ArrowWrapper = styled.button<ArrowObject>`
  position: absolute;
  top: 0;
  width: 207px;
  height: 100%;
  transform: none;
  transition: opacity ${baseTransitionSecond / 2}s linear;
  ${mq.onlysp} {
    display: none;
  }
  &::after {
    content: '';
    position: absolute;
    display: block;
    top: 0;
    bottom: 0;
    right: 0;
    left: 0;
    opacity: 0;
    mix-blend-mode: multiply;
    background-color: #3b4959;
    transition: opacity ${baseTransitionSecond}s ease;
    
  }
  ${props =>
    props.dir === 'prev'
      ? `
          left: 0;
          z-index: 1;
        `
      : `
          right: 0;
        `}

  &:hover {
    &::after {
      opacity: 0.3;
    }
  }
`;

const ArrowInner = styled.div<ArrowInnerObject>`
  content: '';
  display: block;
  position: absolute;
  left: 0;
  top: 0;
  width: 66px;
  margin: auto;
  height: 66px;
  background-image: url(${arrowIcon});
  background-repeat: no-repeat;
  background-position: center;
  opacity: 0;
  transform: scale3d(0.6, 0.6, 1);
  filter: drop-shadow(1px 1px 0 #fff);
  
  ${props =>
    props.dir === 'prev' &&
    `
      transform: rotate(180deg);
    `}
`;

const Arrow = (props: ArrowObject) => {
  const [init, setinit] = useState(false);
  const [rectX, setRectX] = useState(0);
  const [rectY, setRectY] = useState(0);
  const arrow = useRef<HTMLButtonElement>(null);
  const arrowInner = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (arrow.current && !init) {
      _init();
      setinit(true);
      window.addEventListener('resize', _init);
    }
    return () => {
      window.removeEventListener('resize', _init);
    };
  }, []);
  const _init = () => {
    const rect = arrow.current!.getBoundingClientRect();
    setRectX(rect.left);
    setRectY(rect.top);
  };
  const _getPosX = (e: any) => {
    const xPos = e.pageX - rectX - arrowInner.current!.clientWidth / 2;
    return xPos;
  };
  const _getPosY = (e: any) => {
    setRectY(arrow.current!.getBoundingClientRect().top + window.pageYOffset);
    const yPos = e.pageY - rectY - arrowInner.current!.clientHeight / 2;
    return yPos;
  };
  const _mouseEnter: any = (e: any) => {
    TweenMax.to(arrowInner.current, baseTransitionSecond * 2, { opacity: 1, scale: 1, x: _getPosX(e), y: _getPosY(e) });
  };
  const _mouseMove: any = (e: any) => {
    TweenMax.to(arrowInner.current, baseTransitionSecond * 2, { x: _getPosX(e), y: _getPosY(e) });
  };
  const _mouseLeave: any = () => {
    TweenMax.to(arrowInner.current, baseTransitionSecond * 2, { opacity: 0, scale: 0.6 });
  };
  return (
    <ArrowWrapper
      ref={arrow}
      onClick={props.onClick}
      dir={props.dir}
      onMouseEnter={_mouseEnter}
      onMouseMove={_mouseMove}
      onMouseLeave={_mouseLeave}
    >
      <ArrowInner ref={arrowInner} dir={props.dir} slideType={props.slideType}></ArrowInner>
    </ArrowWrapper>
  );
};

const GallerySlide: React.FC<GallerySlideProps> = props => {
  const [currentIndex, setCurrentIndex] = useState(1);
  const slider = useRef<Slider>(null);

  const settings = {
    dots: false,
    infinite: true,
    speed: 800,
    slidesToShow: 1,
    slidesToScroll: 1,
    autoplay: true,
    autoplaySpeed: AUTOPLAYSPEED,
    nextArrow: <Arrow dir={'next'} slideType={props.type} />,
    prevArrow: <Arrow dir={'prev'} slideType={props.type} />,
    beforeChange: (current: number, next: number) => {
      setCurrentIndex(next + 1);
    },
  };
  const progressbars = [];
  for (let idx = 0; idx < props.contents.length; idx++) {
    progressbars.push(
      <Progressbar key={idx} className={currentIndex === idx + 1 ? 'is-active' : undefined}></Progressbar>
    );
  }
  return (
    <Container>
      <SliderWrapper {...props}>
        <Slider {...settings} ref={slider}>
          {props.contents.map((item, idx) =>
            item.media === 'image' ? (
              <Img key={idx} filename={item.path} alt={item.alt ? item.alt : ''}></Img>
            ) : (
              <Video key={idx} url={item.path}></Video>
            )
          )}
        </Slider>
      </SliderWrapper>
      <Controler>
        <Title>{props.contents[currentIndex - 1].title ? props.contents[currentIndex - 1].title : ''}</Title>
        <ControlerInner>
          <Index>
            <span>{currentIndex}</span>
            <span> {props.contents.length}</span>
          </Index>
          <Progressbars>{progressbars}</Progressbars>
        </ControlerInner>
      </Controler>
    </Container>
  );
};

export default GallerySlide;
